import Button from "@/components/Button";
import IconButton from "@/components/IconButton";
import TextField from "@/components/TextField";

export type NotificationEmailField = {
  id: string;
  value: string;
};

type NotificationEmailListProps = {
  emails: NotificationEmailField[];
  canEdit: boolean;
  disabled?: boolean;
  addTestId: string;
  fieldTestIdPrefix: string;
  fieldLabel: string;
  addLabel: string;
  removeAriaLabel: string;
  onChange: (emails: NotificationEmailField[]) => void;
  errors?: Array<string | undefined>;
};

export function NotificationEmailList({
  emails,
  canEdit,
  disabled = false,
  addTestId,
  fieldTestIdPrefix,
  fieldLabel,
  addLabel,
  removeAriaLabel,
  onChange,
  errors = []
}: NotificationEmailListProps) {
  const fields = emails.length > 0 ? emails : [{ id: `${fieldTestIdPrefix}-empty`, value: "" }];

  return (
    <div className="flex flex-col gap-6">
      {fields.map((field, index) => {
        const errorText = errors[index];
        const numbered = fields.length > 1;
        const rowLabel = numbered ? `${fieldLabel} ${index + 1}` : fieldLabel;
        const rowRemoveAriaLabel = numbered ? `${removeAriaLabel} ${index + 1}` : removeAriaLabel;
        return (
          <div key={field.id} className="flex w-full min-w-0 items-end gap-2">
            <div className="min-w-0 flex-1">
              <TextField
                className="w-full"
                label={rowLabel}
                value={field.value}
                disabled={disabled}
                readOnly={!canEdit}
                error={Boolean(errorText)}
                errorText={errorText}
                onChangeText={(value) => {
                  const next = [...fields];
                  next[index] = { ...field, value };
                  onChange(next);
                }}
              />
            </div>
            {canEdit && (
              <IconButton
                name="delete"
                type="button"
                className="mb-1 shrink-0"
                skipMargin
                ariaLabel={rowRemoveAriaLabel}
                disabled={disabled}
                data-testid={`${fieldTestIdPrefix}-remove-${index}`}
                onClick={() => {
                  const remaining = fields.filter((row) => row.id !== field.id);
                  onChange(
                    remaining.length > 0
                      ? remaining
                      : [{ id: `${fieldTestIdPrefix}-${crypto.randomUUID()}`, value: "" }]
                  );
                }}
              />
            )}
          </div>
        );
      })}
      {canEdit && (
        <div>
          <Button
            type="button"
            variant="filledTonal"
            icon="add"
            disabled={disabled}
            testId={addTestId}
            onClick={() => onChange([...fields, { id: `${fieldTestIdPrefix}-${crypto.randomUUID()}`, value: "" }])}
          >
            {addLabel}
          </Button>
        </div>
      )}
    </div>
  );
}
